import * as React from "react";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkScrim } from "uitk-react-scrim";
import { UitkSubheading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";

import { HotelImageCardProps } from "components/flexComponents/Hotels/typings";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";
import { useLocalization } from "@shared-ui/localization-context";

const HotelCard = (props: HotelImageCardProps) => {
  const { context, model, hotel } = props;
  const { callToAction, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  return (
    <UitkCard>
      <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
        <UitkImage src={hotelImageUrl} alt={hotelName} placeholderImage lazyLoading="lazy" />
        <UitkScrim>
          <UitkSubheading tag="h3">
            <UitkText inline size={500}>
              {hotelName}
            </UitkText>
          </UitkSubheading>
          <UitkRating
            rating={hotelStarRating}
            icon={ratingIcon(context.locale, context.site.id)}
            className={context.site.brand}
          />
          <UitkText size={300}>{hotelFormattedFullAddress}</UitkText>
        </UitkScrim>
      </UitkFigure>

      <UitkCardContentSection>
        <UitkLayoutFlex justifyContent="space-between">
          <UitkLayoutFlex direction="column">
            <UitkSpacing margin={{ blockend: "four" }}>
              <ValueProposition hotel={hotel} />
            </UitkSpacing>
            <DistanceOrRating model={model} hotel={hotel} context={context} />
          </UitkLayoutFlex>
          <UitkLayoutFlex>
            <PriceLockup
              hotel={hotel}
              hotelLink={getHotelLink(hotel, model.destinationId, context)}
              priceA11y={new HotelPriceLockup(hotel, formatText)}
              hasGetRatesText
              textAlignment="right"
            />
          </UitkLayoutFlex>
        </UitkLayoutFlex>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <DescriptionOrReview model={model} hotel={hotel} />
        <UitkSpacing margin={{ blockstart: "three" }}>
          <AmenitiesOrRecommendation model={model} hotel={hotel} />
        </UitkSpacing>
      </UitkCardContentSection>

      <UitkCardLink>
        <TrackedLink
          data-testid={`hotelcard-link-${hotelName}`}
          aria-label={hotelName}
          moduleName="hotels"
          rfrr="card"
          rel={getHotelLinkRel(callToAction)}
          href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
        />
      </UitkCardLink>
    </UitkCard>
  );
};

export default HotelCard;
